import React, {
    useState, useEffect, useRef, useCallback, useContext, memo
} from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css'; // Import PDF viewer styles
import {
    Button
} from 'shared/components/ui/button';
import {
    ZoomIn, ZoomOut, ArrowLeft, ArrowRight, Maximize, Monitor
} from 'lucide-react';
import {
    debounce
} from 'lodash';
import {
    SidebarContext
} from '../knowledgeBuilderContext/KBSidebarContext';
import {
    Tooltip,
    TooltipProvider,
    TooltipTrigger,
} from 'shared/components/ui/tooltip';
import KBTooltipContent from './KBTooltipContent';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.2.67/pdf.worker.min.mjs';

const PDFViewerWithZoom = memo(({
    document, page
}) => {
    const [pdfDoc, setPdfDoc] = useState(null);
    const [scale, setScale] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const [iframeError, setIframeError] = useState(false);
    const [isFitWidth, setIsFitWidth] = useState(true); // Default to Fit Width

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const renderTaskRef = useRef(null);
    const [viewedPage, setViewedPage] = useState(page);

    const SBContext = useContext(SidebarContext);

    // Calculate scale to fit width
    const calculateScaleToFit = useCallback((pageInstance) => {
        const container = containerRef.current;
        if (!container || !pageInstance) return 1;

        const viewport = pageInstance.getViewport({
            scale: 1
        });
        const containerWidth = container.clientWidth;

        // Subtract a small buffer for scrollbar if needed, or just use clientWidth
        // If overflow-y is auto, clientWidth excludes scrollbar.
        const scaleX = containerWidth / viewport.width;

        return scaleX;
    }, []);

    // Load PDF document
    useEffect(() => {
        const loadPdf = async () => {
            try {
                const loadingTask = pdfjsLib.getDocument(document?.presigned_url || '');
                const pdf = await loadingTask.promise;
                setPdfDoc(pdf);
                setNumPages(pdf.numPages);
                setIsFitWidth(true); // Default to Fit Width
                // setScale will be handled by the fit width effect
            } catch (error) {
                console.error('Error loading PDF:', error);
                setIframeError(true);
            }
        };

        if (document?.presigned_url) {
            loadPdf();
        }

        return () => {
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
            }
        };
    }, [document]);

    // Handle Fit Width Logic
    useEffect(() => {
        if (isFitWidth && pdfDoc) {
            pdfDoc.getPage(viewedPage).then(pdfPage => {
                const newScale = calculateScaleToFit(pdfPage);
                setScale(newScale);
            });
        }
    }, [isFitWidth, pdfDoc, viewedPage, calculateScaleToFit]);

    // Handle Resize
    useEffect(() => {
        const handleResize = debounce(() => {
            if (isFitWidth && pdfDoc) {
                pdfDoc.getPage(viewedPage).then(pdfPage => {
                    const newScale = calculateScaleToFit(pdfPage);
                    setScale(newScale);
                });
            }
        }, 100);

        const resizeObserver = new ResizeObserver(handleResize);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
            handleResize.cancel();
        };
    }, [isFitWidth, pdfDoc, viewedPage, calculateScaleToFit]);

    // Render PDF page
    const renderPage = async (pdf, pageNum, currentScale) => {
        if (!pdf || !canvasRef.current) return;

        try {
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
            }

            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            // Don't clear rect immediately to avoid flicker, let render handle it?
            // Actually clearing is safer for transparency.
            context.clearRect(0, 0, canvas.width, canvas.height);

            const pdfPage = await pdf.getPage(pageNum);

            const viewport = pdfPage.getViewport({
                scale: currentScale
            });

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            renderTaskRef.current = pdfPage.render(renderContext);
            await renderTaskRef.current.promise;
            renderTaskRef.current = null;

        } catch (error) {
            if (error.name !== 'RenderingCancelled') {
                console.error('Error rendering PDF page:', error);
            }
        }
    };

    // Trigger render when scale or page changes
    useEffect(() => {
        if (pdfDoc) {
            renderPage(pdfDoc, viewedPage, scale);
        }
    }, [viewedPage, scale, pdfDoc]);

    // Handle zoom in
    const handleZoomIn = () => {
        setIsFitWidth(false);
        const newScale = Math.min(scale * 1.2, 5.0);
        setScale(newScale);
    };

    // Handle zoom out
    const handleZoomOut = () => {
        setIsFitWidth(false);
        const newScale = Math.max(scale / 1.2, 0.1);
        setScale(newScale);
    };

    // Set to 100%
    const handleActualSize = () => {
        setIsFitWidth(false);
        setScale(1);
    };

    // Set to Fit Width
    const handleFitWidth = () => {
        setIsFitWidth(true);
    };

    // Navigate to previous page
    const prevPage = () => {
        if (viewedPage > 1) {
            setViewedPage((prev) => prev - 1);
        }
    };

    // Navigate to next page
    const nextPage = () => {
        if (viewedPage < numPages) {
            setViewedPage((prev) => prev + 1);
        }
    };

    // Sync with sidebar selection
    useEffect(() => {
        if (SBContext?.selectedQuery) {
            const newPage = SBContext?.selectedQuery.page_number;
            if (newPage && newPage !== viewedPage) {
                setViewedPage(newPage);
            }
        }
    }, [SBContext?.selectedQuery]);

    if (iframeError) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-4">
                <p className="text-red-500 mb-4">Unable to load PDF document.</p>
            </div>
        );
    }

    if (!document?.presigned_url) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-4">
                <p>No document available.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-2 bg-gray-100 border-b shrink-0">
                <div className="flex items-center space-x-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={prevPage}
                        disabled={viewedPage <= 1}
                        className="h-8 w-8"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">
                        {viewedPage} / {numPages}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={nextPage}
                        disabled={viewedPage >= numPages}
                        className="h-8 w-8"
                    >
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </div>
                <div className="flex items-center space-x-1">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant={isFitWidth ? 'secondary' : 'ghost'}
                                    size="icon"
                                    onClick={handleFitWidth}
                                    className="h-8 w-8"
                                >
                                    <Maximize className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <KBTooltipContent tooltipText="Fit Width" />
                        </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant={scale === 1 && !isFitWidth ? 'secondary' : 'ghost'}
                                    size="icon"
                                    onClick={handleActualSize}
                                    className="h-8 w-8"
                                >
                                    <Monitor className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <KBTooltipContent tooltipText="Actual Size (100%)" />
                        </Tooltip>
                    </TooltipProvider>

                    <div className="h-4 w-px bg-gray-300 mx-2" />

                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleZoomOut}
                        disabled={scale <= 0.1}
                        className="h-8 w-8"
                    >
                        <ZoomOut className="h-4 w-4" />
                    </Button>
                    <span className="text-sm w-12 text-center font-medium">
                        {Math.round(scale * 100)}%
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleZoomIn}
                        disabled={scale >= 5.0}
                        className="h-8 w-8"
                    >
                        <ZoomIn className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* PDF Viewer */}
            <div
                ref={containerRef}
                className="flex-grow overflow-auto bg-gray-100 relative w-full"
            >
                <div className={`min-h-full flex ${isFitWidth ? 'justify-start' : 'justify-center'}`}>
                    <canvas ref={canvasRef} className="shadow-lg block" />
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.document?.presigned_url === nextProps.document?.presigned_url &&
        prevProps.page === nextProps.page
    );
});

export default PDFViewerWithZoom;